import { Injectable, Injector, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { Layout } from '../models';
import { setStyle } from '../utils/layout.utils';
import { HttpErrorResponse } from '@angular/common/http';
import { SettingsService } from './settings.service';
import { LayoutStateService } from './layout-state.service';
import { DefaultLayoutConfig } from '../_metronic/configs/default-layout.config';
import * as objectPath from 'object-path';

@Injectable({
	providedIn: 'root',
})
export class LayoutService {
	private renderer: Renderer2;
	private rendererFactory: RendererFactory2;
	private themeSettingsService: SettingsService;
	private layoutState: LayoutStateService;

	private layoutConfigSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
		undefined
	);

	// scope list of css classes
	private classes: Layout.LayoutClasses = {
		header: [],
		header_container: [],
		header_mobile: [],
		header_menu: [],
		aside_menu: [],
		subheader: [],
		subheader_container: [],
		content: [],
		content_container: [],
		footer_container: [],
	};

	// scope list of html attributes
	private attrs = {
		aside_menu: {},
	};

	constructor(private injector: Injector) {
		this.rendererFactory = injector.get(RendererFactory2);
		this.themeSettingsService = injector.get(SettingsService);
		this.layoutState = injector.get(LayoutStateService);
		this.renderer = this.rendererFactory.createRenderer(document.body, null);

	}

	initLayout() {

		this.layoutConfigSubject.next(DefaultLayoutConfig);

		this.layoutState.patch({
			isMenuExpanded: this.getProp('aside.self.display'),
			isNavbarExpanded: this.getProp('extras.toolbar.display'),
			smallScreen: false,
			isSidebarCollapsed: this.getProp('aside.self.minimize.default'),
			isMenuPlacementTop: false,
			isMenuSwitched: false,
			themeSettings: {
				brandColor: this.getProp('brand.self.theme'),
				headerColor: this.getProp('header.self.theme'),
				headerMenuColor: this.getProp('header.menu.self.theme'),
				asideMenuColor: this.getProp('aside.self.theme'),
			} as Layout.ThemeSettings
		});

		// this.fetchThemeSettings(); // 获取远程的配置

	}

	getConfig(): any {
		const config = this.layoutConfigSubject.value;
		if (!config) {
			return DefaultLayoutConfig;
		}
		return config;
	}

	setConfig(config: any) {
		this.layoutConfigSubject.next(config);
	}

	getProp(path: string): any {
		return objectPath.get(this.layoutConfigSubject.value, path);
	}


	setCSSClass(path: string, classesInStr: string) {
		const cssClasses = this.classes[path];
		if (!cssClasses) {
			this.classes[path] = [];
		}
		classesInStr
			.split(' ')
			.forEach((cssClass: string) => this.classes[path].push(cssClass));

		this.layoutState.patch({
			themeSettings: {
				...this.layoutState.get('themeSettings'),
				classes: this.classes,
			} as Layout.ThemeSettings
		})
	}

	getCSSClasses(path: string): string[] {

		const cssClasses = this.classes[path];
		if (!cssClasses) {
			return [];
		}
		return cssClasses;
	}

	getStringCSSClasses(path: string) {
		return this.getCSSClasses(path).join(' ');
	}

	getHTMLAttributes(path: string): any {
		const attributesObj = this.attrs[path];
		if (!attributesObj) {
			return {};
		}
		return attributesObj;
	}

	setHTMLAttribute(path, attrKey: string, attrValue: any) {
		const attributesObj = this.attrs[path];
		if (!attributesObj) {
			this.attrs[path] = {};
		}
		this.attrs[path][attrKey] = attrValue;
	}

	/**
	 * 远程获取配置
	 * @param successFn 
	 */
	fetchThemeSettings(successFn?: (data: Layout.ThemeSettings) => void) {
		this.themeSettingsService.get().subscribe(themeSettings => {
			setStyle((themeSettings.style || 0) + 1, this.injector);

			this.layoutState.patch({ themeSettings });
			if (successFn) {
				successFn(themeSettings);
			}
		});
	}

	updateThemeSettings(
		themeSettings: Layout.ThemeSettings,
		successFn?: (data: Layout.ThemeSettings) => void,
		errorFn?: (error: HttpErrorResponse) => Observable<any>,
	) {
		this.themeSettingsService
			.update(themeSettings)
			.pipe(catchError(e => (errorFn ? errorFn(e) : throwError(e))))
			.subscribe(data => {
				this.layoutState.patch({ themeSettings });
				if (successFn) {
					successFn(data);
				}
			});
	}

	onMouseOverSidebar(value: boolean) {
		this.layoutState.patch({ mouseOnSidebar: value });
	}

	onClickMenuIcon(value: boolean) {
		this.layoutState.patch({ isSidebarCollapsed: value });
		if (value) {
			this.setMenuStatus(Layout.MenuStatus.AlwaysOpened);
		} else {
			this.setMenuStatus(Layout.MenuStatus.OpenOnHover);
		}
	}

	setMenuStatus(menuStatus: Layout.MenuStatus) {
		if (
			this.layoutState.get('menuStatus') === menuStatus &&
			this.layoutState.get('menuPlacement') !== Layout.MenuPlacement.Left
		) {
			return;
		}
		this.layoutState.patch({ menuStatus, isSidebarCollapsed: !!menuStatus });
	}
}
